import { useCallback, useEffect, useState } from "react";
import { BarChart3, Download, Pencil } from "lucide-react";
import api from "@/lib/api";
import QuestionEditor from "@/components/admin/QuestionEditor";

const SUBJECTS = ["", "Maths", "English", "French", "Science", "History & Geography", "SSEE", "Health & Physical Education"];
const PERIODS = [[7, "Last 7 days"], [30, "Last 30 days"], [0, "All time"]];
const pill = (on) => `px-3 py-1.5 rounded-full border-2 text-sm font-semibold ${on ? "bg-yellow-300 text-slate-900 border-yellow-300" : "border-slate-600"}`;

// Admin: questions where most children pressed "Ed mwa" — candidates to reword.
export default function HelpStats() {
  const [days, setDays] = useState(30);
  const [grade, setGrade] = useState(0);
  const [subject, setSubject] = useState("");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const params = { days, ...(grade ? { grade } : {}), ...(subject ? { subject } : {}) };

  const load = useCallback(() => {
    api.get("/admin/help-stats", { params: { days, ...(grade ? { grade } : {}), ...(subject ? { subject } : {}) } })
      .then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [days, grade, subject]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    const { data } = await api.get("/admin/help-stats/export", { params, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "anou-appran-help-stats.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-2xl border border-slate-700 p-5 space-y-3" data-testid="help-stats">
      <h3 className="text-lg font-bold inline-flex items-center gap-2"><BarChart3 size={18} /> Most help needed</h3>
      <p className="text-sm text-slate-400">Questions where the most children pressed "Ed mwa". These may need clearer wording or a picture.</p>
      <div className="flex gap-2 flex-wrap items-center">
        {PERIODS.map(([d, label]) => <button key={d} onClick={() => setDays(d)} className={pill(days === d)} data-testid={`help-stats-period-${d}`}>{label}</button>)}
        <select value={grade} onChange={(e) => setGrade(Number(e.target.value))} data-testid="help-stats-grade"
          className="rounded-lg bg-slate-900 border border-slate-600 px-2 py-1.5 text-sm">
          {[0, 1, 2, 3, 4, 5, 6].map((g) => <option key={g} value={g}>{g ? `Grade ${g}` : "All grades"}</option>)}
        </select>
        <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="help-stats-subject"
          className="rounded-lg bg-slate-900 border border-slate-600 px-2 py-1.5 text-sm">
          {SUBJECTS.map((s) => <option key={s} value={s}>{s || "All subjects"}</option>)}
        </select>
        <button onClick={download} className={pill(false)} data-testid="help-stats-download"><Download size={14} className="inline mr-1" />Download Excel</button>
      </div>
      {items && items.length === 0 && <p className="text-sm text-slate-400" data-testid="help-stats-empty">No hints used in this period yet.</p>}
      {items && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="help-stats-table">
            <thead><tr className="text-left text-slate-400 border-b border-slate-700">
              <th className="py-2 pr-3">Grade</th><th className="pr-3">Subject · Chapter</th><th className="pr-3">Question</th>
              <th className="pr-3 text-right">Children</th><th className="pr-3 text-right">Presses</th><th />
            </tr></thead>
            <tbody>
              {items.map((it, k) => (
                <tr key={`${it.chapter_id}-${it.level}-${it.qi}`} className="border-b border-slate-800 align-top" data-testid={`help-stats-row-${k}`}>
                  <td className="py-2 pr-3">{it.grade}</td>
                  <td className="pr-3 text-slate-300">{it.subject}{it.chapter_title ? ` · ${it.chapter_no}. ${it.chapter_title}` : ""}<div className="text-xs text-slate-500">Level {it.level} · Q{it.qi + 1}</div></td>
                  <td className="pr-3">{it.question}</td>
                  <td className="pr-3 text-right font-bold" data-testid={`help-stats-children-${k}`}>{it.children}</td>
                  <td className="pr-3 text-right">{it.presses}</td>
                  <td><button onClick={() => setEditing({ chapter_id: it.chapter_id, level: it.level, qi: it.qi })} className={pill(false)} data-testid={`help-stats-edit-${k}`}><Pencil size={13} className="inline mr-1" />Edit question</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <QuestionEditor target={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
